import {
  Alert,
  Button,
  DatePicker,
  Drawer,
  Empty,
  Input,
  InputNumber,
  Modal,
  Pagination,
  Radio,
  Select,
  Space,
  Spin,
  Typography,
} from "@arco-design/web-react";
import { Pencil, Plus, RefreshCw, Save, Trash2, UserRound, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { formatContactWorkbenchError } from "../service/crmContactWorkbenchService";
import type {
  ContactGiftItem,
  ContactGiftMutationPayload,
  ContactGiftOption,
  ContactGiftType,
} from "../types";

type GiftDetailDraft = {
  giftOptionId?: string;
  giftName: string;
  quantity: number;
  unitPrice?: string;
};

type GiftDraft = {
  requestId: string;
  giftType: ContactGiftType;
  sentAt: string;
  sendRemark: string;
  receiveRemark: string;
  amount?: number;
  details: GiftDetailDraft[];
};

type ContactGiftModalProps = {
  visible: boolean;
  contactName: string;
  canManageGift: boolean;
  items: ContactGiftItem[];
  total: number;
  page: number;
  pageSize: number;
  options: ContactGiftOption[];
  loading: boolean;
  error?: unknown;
  mutating: boolean;
  onClose: () => void;
  onRefresh: () => void;
  onPageChange: (page: number) => void;
  onCreate: (payload: ContactGiftMutationPayload) => Promise<void>;
  onUpdate: (giftId: string, payload: ContactGiftMutationPayload) => Promise<void>;
  onDelete: (giftId: string) => Promise<void>;
};

const GIFT_TYPE_OPTIONS: Array<{ value: ContactGiftType; label: string }> = [
  { value: "souvenir", label: "伴手礼（40元以下）" },
  { value: "special", label: "特殊礼品" },
];

const GIFT_TYPE_LABELS: Partial<Record<ContactGiftType, string>> = {
  regular: "常规礼品",
  souvenir: "伴手礼（40元以下）",
  special: "特殊礼品",
  official_red_packet: "公众号红包",
  private_red_packet: "私人红包",
};

/** 联系人礼品明细、录入和维护右侧抽屉。 */
export function ContactGiftModal({
  visible,
  contactName,
  canManageGift,
  items,
  total,
  page,
  pageSize,
  options,
  loading,
  error,
  mutating,
  onClose,
  onRefresh,
  onPageChange,
  onCreate,
  onUpdate,
  onDelete,
}: ContactGiftModalProps) {
  const [draft, setDraft] = useState<GiftDraft | null>(null);
  const [editingGiftId, setEditingGiftId] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!visible) {
      setDraft(null);
      setEditingGiftId(undefined);
      setSubmitting(false);
      submittingRef.current = false;
      setFormError("");
    }
  }, [visible]);

  /** 打开空白礼品录入表单。 */
  function openCreate() {
    setEditingGiftId(undefined);
    setDraft(createEmptyGiftDraft());
    setFormError("");
  }

  /** 按历史礼品记录打开编辑表单。 */
  function openEdit(item: ContactGiftItem) {
    setEditingGiftId(item.giftId);
    setDraft({
      requestId: item.giftId,
      giftType: item.giftType,
      sentAt: toDatePickerValue(item.sentAt),
      sendRemark: item.sendRemark ?? "",
      receiveRemark: item.receiveRemark ?? "",
      amount: item.amount === undefined ? undefined : Number(item.amount),
      details: item.details.map((detail) => ({
        giftOptionId: detail.giftOptionId,
        giftName: detail.giftName,
        quantity: detail.quantity,
        unitPrice: detail.unitPrice,
      })),
    });
    setFormError("");
  }

  /** 校验并提交礼品表单。 */
  async function submitDraft() {
    if (!draft || submittingRef.current || submitting || mutating) return;
    const validationError = validateGiftDraft(draft);
    if (validationError) {
      setFormError(validationError);
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setFormError("");
    const payload: ContactGiftMutationPayload = {
      requestId: draft.requestId,
      giftType: draft.giftType,
      sentAt: draft.sentAt,
      sendRemark: draft.sendRemark.trim() || undefined,
      receiveRemark: draft.receiveRemark.trim() || undefined,
      amount: calculateGiftDraftTotal(draft),
      details: isRedPacket(draft.giftType)
        ? []
        : draft.details.map((detail) => ({
            giftOptionId: detail.giftOptionId,
            giftName: detail.giftName.trim(),
            quantity: detail.quantity,
            unitPrice: draft.giftType === "souvenir" ? "40" : detail.unitPrice,
          })),
    };
    try {
      if (editingGiftId) {
        await onUpdate(editingGiftId, payload);
      } else {
        await onCreate(payload);
      }
      setDraft(null);
      setEditingGiftId(undefined);
    } catch (submitError) {
      setFormError(
        formatContactWorkbenchError(submitError, "礼品保存失败，请稍后重试", {
          timeoutMessage: "礼品保存失败，请稍后重试",
        }),
      );
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  /** 二次确认后停用礼品记录。 */
  function confirmDelete(item: ContactGiftItem) {
    Modal.confirm({
      title: "确认删除礼品记录",
      okButtonProps: { status: "danger" },
      onOk: () => onDelete(item.giftId),
    });
  }

  return (
    <Drawer
      title={`${contactName}的礼品记录`}
      visible={visible}
      className="crm-contact-gift-drawer"
      placement="right"
      width={1280}
      style={{ maxWidth: "100vw" }}
      footer={null}
      unmountOnExit
      onCancel={onClose}
    >
      <div className="crm-contact-gift-layout" data-testid="contact-gift-split-layout">
        <section className="crm-contact-gift-history" aria-label="礼品记录">
          <div className="crm-contact-gift-toolbar">
            <div className="crm-contact-gift-history-heading">
              <Typography.Title heading={6}>礼品记录</Typography.Title>
              <Typography.Text type="secondary">共 {total} 条礼品记录</Typography.Text>
            </div>
            <Button icon={<RefreshCw size={15} />} disabled={loading} onClick={onRefresh}>
              刷新
            </Button>
          </div>

          {error ? (
            <Alert
              type="error"
              showIcon
              content="礼品明细加载失败，请重试。"
              action={<Button size="mini" onClick={onRefresh}>重新加载</Button>}
            />
          ) : null}

          <Spin loading={loading} block>
            {items.length === 0 && !loading && !error ? (
              <Empty description="暂无礼品记录" />
            ) : (
              <div className="crm-contact-gift-records">
                {items.map((item, index) => {
                  const totalQuantity = item.details.reduce(
                    (quantity, detail) => quantity + detail.quantity,
                    0,
                  );
                  const totalAmount = calculateGiftTotal(item);
                  return (
                    <article
                      key={item.giftId}
                      className="crm-contact-gift-record"
                      aria-label={`${contactName}的第${index + 1}条礼品记录`}
                    >
                      <header className="crm-contact-gift-record-header">
                        <strong className="crm-contact-gift-record-index">{index + 1}</strong>
                        <span>
                          录入人：
                          <strong>{item.recordedByUserName || "历史数据"}</strong>
                        </span>
                        <Space size={4}>
                          {item.canEdit ? (
                            <Button
                              size="mini"
                              type="text"
                              icon={<Pencil size={14} />}
                              onClick={() => openEdit(item)}
                            >
                              编辑
                            </Button>
                          ) : null}
                          {item.canDelete ? (
                            <Button
                              size="mini"
                              type="text"
                              status="danger"
                              icon={<Trash2 size={14} />}
                              disabled={mutating || submitting}
                              onClick={() => confirmDelete(item)}
                            >
                              删除
                            </Button>
                          ) : null}
                        </Space>
                      </header>

                      <div className="crm-contact-gift-record-contact">
                        <UserRound aria-hidden="true" size={17} />
                        <strong>{contactName}</strong>
                      </div>

                      <div className="crm-contact-gift-record-content">
                        <strong>
                          {formatGiftType(item.giftType)} · {formatGiftDate(item.sentAt)}
                        </strong>
                        {isRedPacket(item.giftType) ? (
                          <span>金额：¥{formatGiftMoney(item.amount ?? "0")}</span>
                        ) : (
                          <>
                            {item.details.map((detail) => {
                              const unitPrice = getGiftUnitPrice(item.giftType, detail.unitPrice);
                              return (
                                <div key={detail.detailId} className="crm-contact-gift-record-detail">
                                  <span>
                                    {detail.giftName} × {detail.quantity}
                                    {item.giftType === "souvenir"
                                      ? ` × ¥${formatGiftMoney(unitPrice ?? "40")}`
                                      : null}
                                  </span>
                                  {item.giftType !== "souvenir" && unitPrice !== undefined ? (
                                    <small>单价 ¥{formatGiftMoney(unitPrice)}</small>
                                  ) : null}
                                </div>
                              );
                            })}
                            {item.giftType === "souvenir" ? (
                              <strong>
                                共 {totalQuantity} 件
                                {totalAmount === undefined
                                  ? null
                                  : ` 合计：¥${formatGiftMoney(totalAmount)}`}
                              </strong>
                            ) : (
                              <strong className="crm-contact-gift-record-summary">
                                <span>共 {totalQuantity} 件</span>
                                {totalAmount === undefined ? null : (
                                  <span>合计 ¥{formatGiftMoney(totalAmount)}</span>
                                )}
                              </strong>
                            )}
                          </>
                        )}
                      </div>

                      {item.sendRemark || item.receiveRemark ? (
                        <dl className="crm-contact-gift-record-remarks">
                          {item.sendRemark ? (
                            <div>
                              <dt>赠送备注</dt>
                              <dd>{item.sendRemark}</dd>
                            </div>
                          ) : null}
                          {item.receiveRemark ? (
                            <div>
                              <dt>收礼后备注</dt>
                              <dd>{item.receiveRemark}</dd>
                            </div>
                          ) : null}
                        </dl>
                      ) : null}
                    </article>
                  );
                })}
              </div>
            )}
          </Spin>
          {total > pageSize ? (
            <nav aria-label="联系人礼品分页">
              <Pagination
                current={page}
                pageSize={pageSize}
                total={total}
                showTotal
                onChange={onPageChange}
              />
            </nav>
          ) : null}
        </section>

        <section className="crm-contact-gift-information" aria-label="礼品信息">
          {draft ? (
            <GiftEditor
              draft={draft}
              options={options}
              error={formError}
              submitting={submitting || mutating}
              onChange={setDraft}
              onCancel={() => {
                setDraft(null);
                setEditingGiftId(undefined);
                setFormError("");
              }}
              onSubmit={() => void submitDraft()}
            />
          ) : (
            <>
              <div className="crm-contact-gift-editor-header">
                <strong>礼品信息</strong>
                {canManageGift ? (
                  <Button type="primary" icon={<Plus size={15} />} onClick={openCreate}>
                    新增礼品
                  </Button>
                ) : null}
              </div>
              <div className="crm-contact-gift-information-empty">
                <Empty description="暂无礼品信息" />
              </div>
            </>
          )}
        </section>
      </div>
    </Drawer>
  );
}

/** 礼品新增/编辑表单。 */
function GiftEditor({
  draft,
  options,
  error,
  submitting,
  onChange,
  onCancel,
  onSubmit,
}: {
  draft: GiftDraft;
  options: ContactGiftOption[];
  error: string;
  submitting: boolean;
  onChange: (draft: GiftDraft) => void;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  const redPacket = isRedPacket(draft.giftType);
  const officialRedPacket = draft.giftType === "official_red_packet";
  const showSentAt = !officialRedPacket;
  const showReceiveRemark = !officialRedPacket;

  return (
    <section className="crm-contact-gift-editor" aria-label="礼品录入">
      <div className="crm-contact-gift-editor-header">
        <strong>礼品信息</strong>
        <Button type="text" icon={<X size={15} />} aria-label="关闭礼品录入" onClick={onCancel} />
      </div>
      <div className="crm-contact-gift-type-field">
        <span className="crm-contact-gift-field-label">礼品类型</span>
        <Radio.Group
          aria-label="礼品类型"
          className="crm-contact-gift-type-group"
          type="button"
          mode="outline"
          value={draft.giftType}
          onChange={(giftType) =>
            onChange({
              ...draft,
              giftType: giftType as ContactGiftType,
              amount: undefined,
              details: isRedPacket(giftType as ContactGiftType)
                ? []
                : [createEmptyDetail()],
            })
          }
        >
          {GIFT_TYPE_OPTIONS.map((option) => (
            <Radio key={option.value} value={option.value}>
              {option.label}
            </Radio>
          ))}
        </Radio.Group>
      </div>

      {!redPacket ? (
        <div className="crm-contact-gift-detail-editor">
          {draft.details.map((detail, index) => (
            <div
              className={`crm-contact-gift-detail-row${
                draft.giftType === "special" ? " crm-contact-gift-detail-row--priced" : ""
              }`}
              key={index}
            >
              <label className="crm-contact-gift-detail-field">
                <span>礼品名称 <b>*</b></span>
                {draft.giftType === "regular" ? (
                  <Select
                    placeholder="选择礼品"
                    value={detail.giftOptionId}
                    onChange={(giftOptionId) => {
                      const option = options.find((item) => item.giftOptionId === giftOptionId);
                      onChange({
                        ...draft,
                        details: draft.details.map((item, itemIndex) =>
                          itemIndex === index
                            ? {
                                ...item,
                                giftOptionId,
                                giftName: option?.giftName ?? "",
                                unitPrice: option?.unitPrice,
                              }
                            : item,
                        ),
                      });
                    }}
                  >
                    {options.map((option) => (
                      <Select.Option key={option.giftOptionId} value={option.giftOptionId}>
                        {option.giftName}
                      </Select.Option>
                    ))}
                  </Select>
                ) : (
                  <Input
                    placeholder="输入礼品名称"
                    value={detail.giftName}
                    onChange={(giftName) =>
                      onChange({
                        ...draft,
                        details: draft.details.map((item, itemIndex) =>
                          itemIndex === index
                            ? { ...item, giftOptionId: undefined, giftName }
                            : item,
                        ),
                      })
                    }
                  />
                )}
              </label>
              <label className="crm-contact-gift-detail-field">
                <span>礼品数量 <b>*</b></span>
                <InputNumber
                  aria-label={`礼品数量 ${index + 1}`}
                  min={1}
                  precision={0}
                  value={detail.quantity}
                  onChange={(quantity) =>
                    onChange({
                      ...draft,
                      details: draft.details.map((item, itemIndex) =>
                        itemIndex === index ? { ...item, quantity: Number(quantity ?? 1) } : item,
                      ),
                    })
                  }
                />
              </label>
              {draft.giftType === "special" ? (
                <label className="crm-contact-gift-detail-field">
                  <span>礼品价格 <b>*</b></span>
                  <InputNumber
                    placeholder="礼品单价"
                    min={0}
                    precision={2}
                    value={detail.unitPrice === undefined ? undefined : Number(detail.unitPrice)}
                    onChange={(unitPrice) =>
                      onChange({
                        ...draft,
                        details: draft.details.map((item, itemIndex) =>
                          itemIndex === index
                            ? {
                                ...item,
                                unitPrice:
                                  unitPrice === undefined ? undefined : String(unitPrice),
                              }
                            : item,
                        ),
                      })
                    }
                  />
                </label>
              ) : null}
              <Button
                className="crm-contact-gift-detail-row-action crm-contact-gift-detail-remove"
                type="text"
                status="danger"
                icon={<Trash2 size={15} />}
                aria-label={`移除礼品 ${index + 1}`}
                disabled={draft.details.length === 1}
                onClick={() =>
                  onChange({
                    ...draft,
                    details: draft.details.filter((_, itemIndex) => itemIndex !== index),
                  })
                }
              />
            </div>
          ))}
          <div
            className={`crm-contact-gift-detail-add-row${
              draft.giftType === "special" ? " crm-contact-gift-detail-add-row--priced" : ""
            }`}
          >
            <Button
              className="crm-contact-gift-detail-row-action"
              type="text"
              icon={<Plus size={15} />}
              onClick={() => onChange({ ...draft, details: [...draft.details, createEmptyDetail()] })}
            >
              增加
            </Button>
          </div>
        </div>
      ) : null}

      {redPacket ? (
        <label className="crm-contact-workbench-field crm-contact-gift-amount-field">
          <span>红包金额 <b>*</b></span>
          <InputNumber
            aria-label="红包金额"
            placeholder="请输入"
            min={0}
            precision={2}
            value={draft.amount}
            prefix="¥"
            onChange={(amount) =>
              onChange({ ...draft, amount: amount === undefined ? undefined : Number(amount) })
            }
          />
        </label>
      ) : null}

      <div
        className={`crm-contact-gift-remarks${
          showReceiveRemark ? "" : " crm-contact-gift-remarks--single"
        }`}
      >
        <label className="crm-contact-workbench-field">
          <span>赠送备注</span>
          <Input.TextArea
            placeholder="请输入"
            autoSize={{ minRows: 2, maxRows: 4 }}
            maxLength={8000}
            value={draft.sendRemark}
            onChange={(sendRemark) => onChange({ ...draft, sendRemark })}
          />
        </label>
        {showReceiveRemark ? (
          <label className="crm-contact-workbench-field">
            <span>收礼后备注</span>
            <Input.TextArea
              placeholder="请输入"
              autoSize={{ minRows: 2, maxRows: 4 }}
              maxLength={8000}
              value={draft.receiveRemark}
              onChange={(receiveRemark) => onChange({ ...draft, receiveRemark })}
            />
          </label>
        ) : null}
      </div>
      {error ? <Alert type="error" showIcon content={error} /> : null}
      <div className="crm-contact-gift-editor-footer">
        {showSentAt ? (
          <label className="crm-contact-workbench-field crm-contact-gift-footer-date">
            <span>送礼日期 <b>*</b></span>
            <DatePicker
              showTime
              value={draft.sentAt || undefined}
              format="YYYY-MM-DD HH:mm:ss"
              onChange={(sentAt) => onChange({ ...draft, sentAt: sentAt ?? "" })}
            />
          </label>
        ) : null}
        <div className="crm-contact-gift-editor-actions">
          <Button onClick={onCancel}>取消</Button>
          {!redPacket ? (
            <Button
              icon={<Trash2 size={15} />}
              onClick={() => onChange({ ...draft, details: [createEmptyDetail()] })}
            >
              清空
            </Button>
          ) : null}
          <Button
            type="primary"
            icon={<Save size={15} />}
            loading={submitting}
            disabled={submitting}
            onClick={onSubmit}
          >
            保存礼品
          </Button>
        </div>
      </div>
    </section>
  );
}

/** 汇总每条礼品明细的金额，伴手礼统一按每件 40 元计算。 */
function calculateGiftTotal(item: ContactGiftItem): number | undefined {
  const storedAmount = parseGiftMoney(item.amount);
  if (
    item.details.length === 0 ||
    (item.giftType !== "souvenir" &&
      item.details.some((detail) => detail.unitPrice === undefined))
  ) {
    return storedAmount;
  }
  const calculatedAmount = item.details.reduce(
    (total, detail) =>
      total + Number(getGiftUnitPrice(item.giftType, detail.unitPrice)) * detail.quantity,
    0,
  );
  return Number.isFinite(calculatedAmount) ? calculatedAmount : storedAmount;
}

/** 汇总当前表单的礼品金额并使用两位小数写入主记录。 */
function calculateGiftDraftTotal(draft: GiftDraft): string | number | undefined {
  if (isRedPacket(draft.giftType)) {
    return draft.amount;
  }
  if (draft.details.length === 0) {
    return undefined;
  }
  const totalCents = draft.details.reduce((total, detail) => {
    const unitPrice = draft.giftType === "souvenir" ? 40 : Number(detail.unitPrice);
    return total + Math.round(unitPrice * 100) * detail.quantity;
  }, 0);
  return Number.isFinite(totalCents) ? (totalCents / 100).toFixed(2) : undefined;
}

/** 将主记录金额解析为可展示数字。 */
function parseGiftMoney(value?: string): number | undefined {
  if (value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/** 返回展示与计算使用的礼品单价。 */
function getGiftUnitPrice(giftType: ContactGiftType, unitPrice?: string) {
  return giftType === "souvenir" ? "40" : unitPrice;
}

/** 将礼品金额格式化为两位小数。 */
function formatGiftMoney(value: string | number): string {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue.toFixed(2) : String(value);
}

/** 创建一条空礼品表单。 */
function createEmptyGiftDraft(): GiftDraft {
  return {
    requestId: createRequestId(),
    giftType: "souvenir",
    sentAt: formatLocalDateTime(new Date()),
    sendRemark: "",
    receiveRemark: "",
    details: [createEmptyDetail()],
  };
}

/** 创建一条空礼品明细。 */
function createEmptyDetail(): GiftDetailDraft {
  return { giftName: "", quantity: 1 };
}

/** 校验礼品表单的必填和分支字段。 */
function validateGiftDraft(draft: GiftDraft) {
  if (!draft.sentAt) return "请选择赠送时间";
  if (isRedPacket(draft.giftType)) {
    return draft.amount === undefined || draft.amount < 0 ? "请输入有效的红包金额" : "";
  }
  if (!draft.details.length || draft.details.some((item) => !item.giftName.trim() || item.quantity < 1)) {
    return "请至少选择一项礼品并填写有效数量";
  }
  if (
    draft.giftType === "special" &&
    draft.details.some((item) => item.unitPrice === undefined || Number(item.unitPrice) < 0)
  ) {
    return "请填写特殊礼品单价";
  }
  return "";
}

/** 判断礼品类型是否为红包。 */
function isRedPacket(giftType: ContactGiftType) {
  return giftType === "official_red_packet" || giftType === "private_red_packet";
}

/** 格式化礼品类型。 */
function formatGiftType(giftType: ContactGiftType) {
  return GIFT_TYPE_LABELS[giftType] ?? giftType;
}

/** 将礼品时间格式化为卡片日期。 */
function formatGiftDate(value?: string) {
  return value?.slice(0, 10) || "-";
}

/** 转为日期控件可识别的时间文本。 */
function toDatePickerValue(value?: string) {
  return value?.replace("T", " ").slice(0, 19) ?? "";
}

/** 生成后端幂等写入使用的 UUID。 */
function createRequestId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    const value = character === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

/** 格式化本地日期时间。 */
function formatLocalDateTime(value: Date) {
  const pad = (part: number) => String(part).padStart(2, "0");
  return [
    value.getFullYear(),
    "-",
    pad(value.getMonth() + 1),
    "-",
    pad(value.getDate()),
    " ",
    pad(value.getHours()),
    ":",
    pad(value.getMinutes()),
    ":00",
  ].join("");
}
